import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { T, IMAGES } from "@/content/site";
import { scrollToId } from "@/hooks/useLenis";

export default function TwoPaths() {
  const { t } = useLang();
  return (
    <section id="two-paths" data-testid="two-paths-section" className="relative bg-[#0F0F0F] border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 pt-24 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline">
          <span className="inline-block w-8 h-px bg-[color:var(--sacrvm-gold)] align-middle mr-3" />
          {t(T.twoPaths.overline)}
        </motion.div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 border-t border-white/10">
        <Panel
          testid="path-left"
          image="https://customer-assets-wrfwihn1.emergentagent.net/job_mezcal-luxury/artifacts/5c2qtk0o_4%20productos.jpg"
          fit="contain"
          title={t(T.twoPaths.left.title)}
          body={t(T.twoPaths.left.body)}
          cta={t(T.twoPaths.left.cta)}
          onClick={() => scrollToId("collection")}
          index="01"
        />
        <Panel
          testid="path-right"
          image={IMAGES.jimador}
          title={t(T.twoPaths.right.title)}
          body={t(T.twoPaths.right.body)}
          cta={t(T.twoPaths.right.cta)}
          onClick={() => scrollToId("private-label")}
          index="02"
          gold
          divider
        />
      </div>
    </section>
  );
}

function Panel({ image, title, body, cta, onClick, index, gold, divider, testid, fit }) {
  const contain = fit === "contain";
  if (contain) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
        data-testid={testid}
        className={"group relative overflow-hidden min-h-[560px] md:min-h-[720px] flex flex-col bg-black " + (divider ? "md:border-l border-white/10" : "")}
      >
        <div className="flex-1 flex items-center justify-center p-6 md:p-10 min-h-0">
          <img
            src={image}
            alt={title}
            className="max-w-full max-h-full object-contain transition-transform duration-[1600ms] group-hover:scale-[1.03]"
          />
        </div>
        <div className="relative p-8 md:p-14 max-w-[560px]">
          <div className={"font-serif text-[22px] mb-6 " + (gold ? "text-[color:var(--sacrvm-gold)]" : "text-white/50")}>{index}</div>
          <h3 className="font-serif text-white text-[40px] md:text-[56px] leading-[1] tracking-[-0.01em] mb-6">{title}</h3>
          <p className="text-white/70 text-[16px] leading-[1.7] mb-8">{body}</p>
          <button onClick={onClick} className={"btn-sharp " + (gold ? "gold" : "")} data-testid={`${testid}-cta`}>
            {cta}
            <ArrowRight size={14} className="arrow" />
          </button>
        </div>
      </motion.div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
      data-testid={testid}
      className={"group relative overflow-hidden min-h-[560px] md:min-h-[720px] flex items-end bg-black " + (divider ? "md:border-l border-white/10" : "")}
    >
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
      <div className="relative p-8 md:p-14 max-w-[520px]">
        <div className={"font-serif text-[22px] mb-6 " + (gold ? "text-[color:var(--sacrvm-gold)]" : "text-white/50")}>{index}</div>
        <h3 className="font-serif text-white text-[40px] md:text-[56px] leading-[1] tracking-[-0.01em] mb-6">
          {title}
        </h3>
        <p className="text-white/70 text-[16px] leading-[1.7] mb-8">{body}</p>
        <button onClick={onClick} className={"btn-sharp " + (gold ? "gold" : "")} data-testid={`${testid}-cta`}>
          {cta}
          <ArrowRight size={14} className="arrow" />
        </button>
      </div>
    </motion.div>
  );
}
